import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { ToolCall } from '../../aiSuggestionsPluginType';

export type ResolvedToolCallPosition = {
	from: number;
	localId: string;
	to: number;
};

/**
 * Given a list of tool calls and the ProseMirror doc, walks the doc once and
 * returns the position and size of every node that is the target of a tool call.
 *
 * Handles both single-localId tool calls (localId) and multi-localId tool calls
 * (localIds, e.g. `removeNodes`).
 */
export function resolveToolCallNodePositions(
	doc: PMNode,
	toolCalls: ToolCall[],
): ResolvedToolCallPosition[] {
	// Build a set of all localIds we need to find
	const targetLocalIds = new Set<string>();
	for (const toolCall of toolCalls) {
		if ('localId' in toolCall.arguments && toolCall.arguments.localId) {
			targetLocalIds.add(toolCall.arguments.localId);
		} else if ('localIds' in toolCall.arguments) {
			for (const localId of toolCall.arguments.localIds) {
				targetLocalIds.add(localId);
			}
		}
	}

	if (targetLocalIds.size === 0) {
		return [];
	}

	const results: ResolvedToolCallPosition[] = [];
	const found = new Set<string>();

	doc.descendants((node, pos) => {
		// Stop early once we've found all targets
		if (found.size === targetLocalIds.size) {
			return false;
		}

		const localId = node.attrs?.localId as string | undefined;
		if (localId && targetLocalIds.has(localId) && !found.has(localId)) {
			found.add(localId);
			results.push({ localId, from: pos, to: pos + node.nodeSize });
		}

		return true;
	});

	return results;
}
